const PROVIDERS = ['openai', 'anthropic', 'azure', 'bedrock'] as const;
export type ProviderName = (typeof PROVIDERS)[number];

/** Global flags consumed by resolveConfig — skipped (with their value) by the arm parser. */
const GLOBAL_VALUE_FLAGS = new Set(['--env', '--api-url', '--token', '--org', '--target-org']);

export type FlagValue = boolean | 'treatment' | 'control';

export type ParsedArm = {
  label: string;
  model?: string;
  provider?: ProviderName;
  knobOverrides?: Record<string, unknown>;
  flagOverrides?: Record<string, FlagValue>;
};

export type ParsedAbArgs = {
  workflowIds: string[];
  all: boolean;
  runsPerArm?: number;
  arms: ParsedArm[];
  json: boolean;
};

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

/**
 * Infer a typed knob value from its string form: `true`/`false` → boolean,
 * integer/float → number, a JSON object/array literal → parsed JSON, else the
 * raw string. The server coerces per knob type at read time, so this only needs
 * to preserve intent.
 */
export function inferKnobValue(raw: string): unknown {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(raw)) return Number(raw);
  if (/^[[{]/.test(raw)) {
    try {
      return JSON.parse(raw);
    } catch {
      /* fall through to string */
    }
  }
  return raw;
}

function parseFlagValue(raw: string): FlagValue | null {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw === 'treatment' || raw === 'control') return raw;
  return null;
}

/** Split `<model>[@<provider>]`; validates the provider against the known set. */
function parseModelToken(raw: string): ParseResult<{ model: string; provider?: ProviderName }> {
  const at = raw.lastIndexOf('@');
  if (at < 0) return { ok: true, value: { model: raw } };
  const model = raw.slice(0, at);
  const provider = raw.slice(at + 1);
  if (!model) return { ok: false, error: `--model "${raw}": missing model id before "@"` };
  if (!PROVIDERS.includes(provider as ProviderName)) {
    return {
      ok: false,
      error: `--model "${raw}": unknown provider "${provider}" (expected ${PROVIDERS.join(', ')})`,
    };
  }
  return { ok: true, value: { model, provider: provider as ProviderName } };
}

/**
 * Parse the grouped `benchmark ab` argv. `--arm <label>` opens a new arm; the
 * `--model` / `--knob` / `--flag` flags that follow attach to the most recent
 * arm. Global flags (`--env`, `--token`, …) are skipped so they can appear
 * anywhere. Pure and exhaustively validated so it can be unit-tested.
 */
export function parseAbArgs(argv: string[]): ParseResult<ParsedAbArgs> {
  const workflowIds: string[] = [];
  const arms: ParsedArm[] = [];
  let all = false;
  let json = false;
  let runsPerArm: number | undefined;

  const need = (i: number, flag: string): string | null =>
    i + 1 < argv.length && !argv[i + 1].startsWith('--') ? argv[i + 1] : `MISSING:${flag}`;
  const currentArm = (): ParsedArm | undefined => arms[arms.length - 1];

  for (let i = 0; i < argv.length; i++) {
    const tok = argv[i];

    if (GLOBAL_VALUE_FLAGS.has(tok)) {
      i++; // skip the value
      continue;
    }

    switch (tok) {
      case '--json':
        json = true;
        break;
      case '--all':
        all = true;
        break;
      case '--workflow': {
        const v = need(i, tok);
        if (v?.startsWith('MISSING:')) return { ok: false, error: '--workflow requires an id' };
        workflowIds.push(v!);
        i++;
        break;
      }
      case '--runs-per-arm': {
        const v = need(i, tok);
        if (v?.startsWith('MISSING:')) return { ok: false, error: '--runs-per-arm requires a number' };
        const n = Number(v);
        if (!Number.isInteger(n) || n < 1 || n > 20) {
          return { ok: false, error: `--runs-per-arm must be an integer 1–20 (got "${v}")` };
        }
        runsPerArm = n;
        i++;
        break;
      }
      case '--arm': {
        const v = need(i, tok);
        if (v?.startsWith('MISSING:')) return { ok: false, error: '--arm requires a label' };
        arms.push({ label: v! });
        i++;
        break;
      }
      case '--model': {
        const arm = currentArm();
        if (!arm) return { ok: false, error: '--model must follow an --arm' };
        const v = need(i, tok);
        if (v?.startsWith('MISSING:')) return { ok: false, error: '--model requires a value' };
        const parsed = parseModelToken(v!);
        if (!parsed.ok) return parsed;
        arm.model = parsed.value.model;
        if (parsed.value.provider) arm.provider = parsed.value.provider;
        i++;
        break;
      }
      case '--knob': {
        const arm = currentArm();
        if (!arm) return { ok: false, error: '--knob must follow an --arm' };
        const v = need(i, tok);
        if (v?.startsWith('MISSING:')) return { ok: false, error: '--knob requires key=value' };
        const eq = v!.indexOf('=');
        if (eq <= 0) return { ok: false, error: `--knob "${v}" must be key=value` };
        (arm.knobOverrides ??= {})[v!.slice(0, eq)] = inferKnobValue(v!.slice(eq + 1));
        i++;
        break;
      }
      case '--flag': {
        const arm = currentArm();
        if (!arm) return { ok: false, error: '--flag must follow an --arm' };
        const v = need(i, tok);
        if (v?.startsWith('MISSING:')) return { ok: false, error: '--flag requires name=value' };
        const eq = v!.indexOf('=');
        if (eq <= 0) return { ok: false, error: `--flag "${v}" must be name=value` };
        const fv = parseFlagValue(v!.slice(eq + 1));
        if (fv === null) {
          return {
            ok: false,
            error: `--flag "${v}": value must be true|false|treatment|control`,
          };
        }
        (arm.flagOverrides ??= {})[v!.slice(0, eq)] = fv;
        i++;
        break;
      }
      default:
        if (tok.startsWith('--')) return { ok: false, error: `Unknown flag: ${tok}` };
        return { ok: false, error: `Unexpected argument: ${tok}` };
    }
  }

  if (arms.length === 0) return { ok: false, error: 'At least one --arm is required' };
  if (arms.length > 4) return { ok: false, error: 'At most 4 arms are supported per run' };
  const labels = arms.map((a) => a.label);
  if (new Set(labels).size !== labels.length) {
    return { ok: false, error: 'Arm labels must be unique' };
  }
  if (!all && workflowIds.length === 0) {
    return { ok: false, error: 'Provide --workflow <id> (repeatable) or --all' };
  }

  return { ok: true, value: { workflowIds, all, runsPerArm, arms, json } };
}

/** Serialize a parsed arm into the ad-hoc endpoint's arm payload shape. */
export function armPayload(arm: ParsedArm): Record<string, unknown> {
  return {
    label: arm.label,
    ...(arm.knobOverrides ? { knobOverrides: arm.knobOverrides } : {}),
    ...(arm.flagOverrides ? { flagOverrides: arm.flagOverrides } : {}),
    ...(arm.provider ? { provider: arm.provider } : {}),
    ...(arm.model ? { model: arm.model } : {}),
  };
}
